import React, { CSSProperties } from 'react';
import styled from 'styled-components';
import { Field, FieldProps, useField } from 'formik';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import { Switch } from '@utmost.co/utmost-design-system';
import { useSmartFormContext } from '../forms/utils/useSmartFormContext';
import { ComponentPropBase } from '../types';
import { Label } from '../atoms';
import { PermissionHelper } from '../atoms/common/PermissionHelper';
import { ReadOnly } from '../atoms/ReadOnly/ReadOnly';

export interface SwitchFieldProps extends ComponentPropBase {
  placeholder?: string;
  fullWidth?: boolean;
  style?: CSSProperties;
  label?: string;
  helpText?: string;
  errors?: string;
  touched?: boolean;
  disabled?: boolean;
  mapInputValue?: (value: any) => any;
  onChange?: (e: React.ChangeEvent<any>, setValue: any) => void;
}

export const SwitchField: React.FC<SwitchFieldProps> = ({
  label,
  name,
  errors,
  helpText,
  infoText,
  touched,
  type,
  placeholder,
  disabled,
  required,
  mapInputValue,
  onChange,
  fullWidth,
  ...rest
}) => {
  const {
    readonly,
    getDisabled,
    isSubmitting: isFormSubmitting,
    setFieldValue,
    setFieldTouched,
    getFieldPermissions,
    permissionDebugger
  } = useSmartFormContext();
  const [input] = useField<boolean | undefined | null>(name || 'INVALID');
  const permissions = getFieldPermissions(name);

  if (readonly) {
    return (
      <Field name={name}>
        {({ field }: FieldProps<boolean>) => (
          <FormControl disabled data-test={`${name}-field-control`} className="field-control">
            <ReadOnly label={label} value={field.value ? 'Yes' : 'No'} />
            {permissionDebugger && <PermissionHelper permission={permissions} />}
          </FormControl>
        )}
      </Field>
    );
  }

  const handleChange = () => {
    if (onChange) {
      onChange(!input.value as any, setFieldValue);
    } else {
      setFieldValue(name, !input.value, false);
    }
    setTimeout(() => {
      setFieldTouched(name, true, true);
    }, 0);
  };

  if (getDisabled(name) || readonly || isFormSubmitting) {
    disabled = true;
  }

  return (
    <div
      style={{ width: fullWidth === false ? 'auto' : '100%' }}
      id={name}
      data-test={`${name}-field-control`}
      className="field-control"
      {...rest}
    >
      {label ? (
        <>
          <Grid container wrap="nowrap" alignItems="center">
            <StyledGrid item>
              <Switch
                inputId={`${name}-input`}
                data-test={`${name}-switch`}
                onChange={handleChange}
                checked={input.value ?? undefined}
                disabled={disabled}
              />
            </StyledGrid>
            <Grid item>
              {/* TODO: We shouldn't use span as interactive element. Refactor to button */}
              {/** biome-ignore lint/a11y/noStaticElementInteractions: legacy code to be refactored */}
              <span style={{ marginLeft: 8, cursor: 'pointer' }} onClick={handleChange}>
                <StyledLabel
                  required={required}
                  infoText={infoText}
                  text={label}
                  dataTest="switch-field-label"
                  htmlFor={`${name}-input`}
                />
              </span>
            </Grid>
          </Grid>
          {!!(helpText || errors) && (
            <FormHelperText error={!!errors && !!touched} data-test={`${name}-field-helper`} id={`${name}-helper-text`}>
              {errors && touched ? errors : helpText}
            </FormHelperText>
          )}
        </>
      ) : (
        <Switch
          onChange={handleChange}
          data-test={`${name}-switch`}
          checked={input.value ?? undefined}
          disabled={disabled}
        />
      )}
      {permissionDebugger && <PermissionHelper permission={permissions} />}
    </div>
  );
};

const StyledGrid = styled(Grid)`
  line-height: initial;
`;
const StyledLabel = styled(Label)`
  margin: 0;
  display: inline-block;
  font-weight: normal;

  label {
    font-weight: normal;
  }
`;

export default SwitchField;
